/**
 * Letter Upload tab of the Delivery Config page. Edits the `letter_upload`
 * section of the per-facility delivery-steps document: document name + status
 * source field, pre-conditions, the Eligible PA Statuses table (with per-bucket
 * document type + filename), and run conditions. Saves back via a partial
 * Firestore write that preserves the rest of the section.
 */
import { useEffect, useMemo, useState } from "react";
import { Button, openModal } from "risa-oasis-ui_v2";
import { LoaderMessage } from "../../../../../components/loaderMessage/loaderMessage";
import QueryBuilder from "../../../../../components/queryBuilder/QueryBuilder";
import {
  CmmInputUniqueValuesProvider,
  useCmmInputUniqueValuesContext,
} from "../../../../../context/cmmInputUniqueValuesContext";
import { buildDeliveryStepConditionConfig } from "../shared/deliveryStepConditionConfig";
import DocumentConfigCard from "../shared/documentConfigCard";
import EligiblePaStatusesTable from "../shared/eligiblePaStatusesTable";
import StepSettingsModal from "../shared/stepSettingsModal";
import AddNewStatusModal, {
  ADD_NEW_LETTER_UPLOAD_STATUS_MODAL_ID,
} from "./addNewStatusModal";
import { useLetterUploadConfig } from "./useLetterUploadConfig";

const LETTER_UPLOAD_SETTINGS_MODAL_ID = "letter-upload-settings-modal";

const ConditionsCard = ({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-3 rounded-lg border border-primaryGray-16 p-4">
    <div className="text-sm font-semibold text-primaryGray-1">{title}</div>
    {subtitle ? (
      <div className="text-tiny text-primaryGray-8">{subtitle}</div>
    ) : null}
    {children}
  </div>
);

const LetterUploadConfigInner = () => {
  const {
    form,
    loading,
    saving,
    error,
    isDirty,
    setDocumentName,
    setStatusSourceField,
    applySettingsForm,
    setPreconditions,
    setConditions,
    updateRow,
    addRow,
    removeRow,
    handleDiscard,
    handleSave,
  } = useLetterUploadConfig();

  const { cmmInputUniqueValues } = useCmmInputUniqueValuesContext();
  const conditionConfig = useMemo(
    () => buildDeliveryStepConditionConfig(cmmInputUniqueValues),
    [cmmInputUniqueValues],
  );

  const [resetKey, setResetKey] = useState(0);
  const [settingsModalKey, setSettingsModalKey] = useState(0);
  const [shouldOpenSettings, setShouldOpenSettings] = useState(false);

  useEffect(() => {
    if (!shouldOpenSettings) {
      return;
    }
    openModal(LETTER_UPLOAD_SETTINGS_MODAL_ID);
    setShouldOpenSettings(false);
  }, [shouldOpenSettings, settingsModalKey]);

  const onDiscard = () => {
    handleDiscard();
    setResetKey((k) => k + 1);
    setSettingsModalKey((k) => k + 1);
  };

  const openSettings = () => {
    setSettingsModalKey((k) => k + 1);
    setShouldOpenSettings(true);
  };

  if (loading) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-primaryGray-16 p-4">
        <div className="flex h-full flex-col gap-4 overflow-hidden rounded-lg bg-white p-6">
          <LoaderMessage message="Loading letter upload configuration..." />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-primaryGray-16 p-4">
      <div className="flex h-full flex-col gap-4 overflow-hidden rounded-lg bg-white p-6">
        <div className="flex flex-shrink-0 flex-row items-start justify-between gap-4 border-b border-primaryGray-16 pb-4">
          <div className="flex flex-col">
            <div className="text-h11 font-bold text-primaryGray-1">
              Letter Upload
            </div>
            <div className="mt-1 text-small text-primaryGray-4">
              Configure the generated letter, eligible PA statuses and their
              documents, and when this delivery step runs.
            </div>
          </div>
          <Button
            buttonType="secondary"
            size="medium"
            onClick={openSettings}
            disabled={Boolean(error)}
            children="Settings"
          />
        </div>

        {error ? (
          <p className="text-body1 text-red-600" role="alert">
            {error}
          </p>
        ) : (
          <div className="mb-1 mt-2 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pb-6">
            <StepSettingsModal
              key={`settings-modal-${settingsModalKey}`}
              dialogId={LETTER_UPLOAD_SETTINGS_MODAL_ID}
              idPrefix="letter-upload"
              title="Letter Upload Settings"
              enabled={form.enabled}
              action={form.action}
              statusField={form.statusField}
              settings={form.settings}
              onApply={applySettingsForm}
            />

            <DocumentConfigCard
              key={`doc-${resetKey}`}
              documentName={form.documentName}
              statusSourceField={form.statusSourceField}
              onDocumentNameChange={setDocumentName}
              onStatusSourceFieldChange={setStatusSourceField}
            />

            <ConditionsCard title="Pre-Conditions">
              <QueryBuilder
                key={`pre-${resetKey}`}
                value={form.preconditions}
                onChange={setPreconditions}
                config={conditionConfig}
              />
            </ConditionsCard>

            <AddNewStatusModal
              usedBuckets={form.paStatusRows.map((row) => row.bucket)}
              onConfirm={addRow}
            />

            <EligiblePaStatusesTable
              tableName="letter-upload-pa-statuses-table"
              rows={form.paStatusRows}
              resetKey={resetKey}
              showDocumentColumns
              readOnlyBucket
              onAdd={() => openModal(ADD_NEW_LETTER_UPLOAD_STATUS_MODAL_ID)}
              onRemove={removeRow}
              onAliasChange={(id, value) => updateRow(id, { alias: value })}
              onIncludeDrugNameChange={(id, value) =>
                updateRow(id, { includeDrugName: value })
              }
              onDocumentTypeChange={(id, value) =>
                updateRow(id, { documentType: value })
              }
              onDocumentNameChange={(id, value) =>
                updateRow(id, { documentName: value })
              }
            />

            <ConditionsCard
              title="Conditions"
              subtitle="This step runs when the conditions below evaluate to true."
            >
              <QueryBuilder
                key={`cond-${resetKey}`}
                value={form.conditions}
                onChange={setConditions}
                config={conditionConfig}
              />
            </ConditionsCard>
          </div>
        )}

        <div className="flex flex-shrink-0 flex-row items-center justify-end gap-2 border-t border-primaryGray-16 pt-2">
          <Button
            buttonType="secondary"
            size="medium"
            onClick={onDiscard}
            disabled={!isDirty || saving}
            children="Cancel"
          />
          <Button
            buttonType="primary"
            size="medium"
            onClick={handleSave}
            disabled={!isDirty || saving}
            children={saving ? "Saving..." : "Save"}
          />
        </div>
      </div>
    </div>
  );
};

/** Letter Upload tab wrapped with the CMM value-search context the QueryBuilder needs. */
const LetterUploadConfig = () => (
  <CmmInputUniqueValuesProvider>
    <LetterUploadConfigInner />
  </CmmInputUniqueValuesProvider>
);

export default LetterUploadConfig;
